// ─── audioUtils.js ────────────────────────────────────────────────────────────
// Часть 1: сжатие аудио в браузере + кеш публичных URL
// ─────────────────────────────────────────────────────────────────────────────

// ═══════════════════════════════════════════════════════════════════════════════
// РАЗДЕЛ 1: Сжатие аудио в браузере перед загрузкой в Supabase
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Форматы, которые уже сжаты — не сжимаем повторно.
 * WAV / AIFF / FLAC — несжатые, жмём в Opus.
 */
const ALREADY_COMPRESSED = new Set(['mp3', 'aac', 'm4a', 'ogg', 'opus', 'webm', 'wma']);
const UNCOMPRESSED       = new Set(['wav', 'aif', 'aiff', 'flac', 'pcm', 'raw']);

/** Находим лучший поддерживаемый кодек */
const detectOpusMime = () => {
  const candidates = [
    'audio/webm;codecs=opus',
    'audio/ogg;codecs=opus',
    'audio/webm',
  ];
  if (typeof MediaRecorder === 'undefined') return null;
  return candidates.find(m => MediaRecorder.isTypeSupported(m)) ?? null;
};

/**
 * Сжать аудиофайл в Opus/WebM прямо в браузере перед отправкой в Supabase.
 *
 * Стратегия:
 * - Уже сжатые форматы (mp3, m4a, ogg, aac) — не трогаем, возвращаем как есть.
 * - WAV/AIFF/FLAC — декодируем через Web Audio API, пишем через MediaRecorder
 *   в Opus (64 кбит/с для речи, 96 кбит/с если файл явно «музыкальный»).
 * - Если браузер не поддерживает MediaRecorder с Opus — возвращаем оригинал.
 *
 * @param {File} file - исходный файл
 * @param {(pct: number|null) => void} onProgress - 0-100 прогресс, null = ошибка
 * @returns {Promise<File>} - сжатый или оригинальный файл
 */
export const compressAudio = (file, onProgress) => {
  return new Promise((resolve) => {
    const ext = file.name.split('.').pop().toLowerCase().replace(/[^a-z]/g, '');

    // ── Пропускаем если формат уже сжатый ──────────────────────────────────
    if (ALREADY_COMPRESSED.has(ext) && !UNCOMPRESSED.has(ext)) {
      onProgress(100);
      resolve(file);
      return;
    }

    const mimeType = detectOpusMime();
    if (!mimeType) {
      // Браузер не поддерживает Opus — загружаем как есть
      console.warn('[AudioCompress] MediaRecorder Opus не поддерживается, пропускаем сжатие');
      onProgress(100);
      resolve(file);
      return;
    }

    // ── Декодируем и сжимаем ────────────────────────────────────────────────
    const reader = new FileReader();

    reader.onload = async (e) => {
      let audioCtx;
      try {
        onProgress(3);
        audioCtx = new (window.AudioContext || window.webkitAudioContext)();

        // Декодируем PCM из файла
        let audioBuffer;
        try {
          audioBuffer = await audioCtx.decodeAudioData(e.target.result.slice(0));
        } catch (decodeErr) {
          console.warn('[AudioCompress] decodeAudioData failed:', decodeErr);
          audioCtx.close();
          onProgress(100);
          resolve(file);
          return;
        }

        onProgress(10);

        const duration     = audioBuffer.duration;           // секунды
        const isLongSpeech = duration > 60;                  // длиннее минуты — скорее речь
        const bitrate      = isLongSpeech ? 64_000 : 96_000; // бит/с

        // Создаём MediaStream через AudioContext
        const dest    = audioCtx.createMediaStreamDestination();
        const source  = audioCtx.createBufferSource();
        source.buffer = audioBuffer;
        source.connect(dest);

        const recorder = new MediaRecorder(dest.stream, {
          mimeType,
          audioBitsPerSecond: bitrate,
        });

        const chunks = [];
        recorder.ondataavailable = (ev) => { if (ev.data.size > 0) chunks.push(ev.data); };

        recorder.onstop = async () => {
          try { audioCtx.close(); } catch {}
          const outExt      = mimeType.includes('webm') ? 'webm' : 'ogg';
          const outName     = file.name.replace(/\.[^/.]+$/, '') + '.' + outExt;
          let compressed    = new Blob(chunks, { type: mimeType });
          // MediaRecorder пишет WebM без длительности: плеер видел «бесконечное» аудио,
          // ползунок стоял, карточки было не расставить. Вписываем длину в заголовок.
          if (outExt === 'webm') {
            try {
              const { default: fixWebmDuration } = await import('fix-webm-duration'); // нужна только здесь — грузим по требованию
              compressed = await fixWebmDuration(compressed, duration * 1000, { logger: false });
            } catch {}
          }
          const ratio       = (file.size / compressed.size).toFixed(1);
          console.log(`[AudioCompress] ${(file.size/1048576).toFixed(1)} МБ → ${(compressed.size/1048576).toFixed(1)} МБ (${ratio}x)`);
          onProgress(100);
          resolve(new File([compressed], outName, { type: mimeType }));
        };

        recorder.onerror = () => {
          try { audioCtx.close(); } catch {}
          console.warn('[AudioCompress] MediaRecorder error, fallback');
          onProgress(100);
          resolve(file);
        };

        // Прогресс по времени
        recorder.start(500); // chunks каждые 500 мс
        source.start(0);
        const started = Date.now();

        const timer = setInterval(() => {
          if (recorder.state === 'inactive') { clearInterval(timer); return; }
          const pct = Math.min(95, 10 + ((Date.now() - started) / (duration * 1000)) * 85);
          onProgress(Math.round(pct));
        }, 400);

        source.onended = () => {
          clearInterval(timer);
          if (recorder.state === 'recording') recorder.stop();
        };

      } catch (err) {
        try { audioCtx?.close(); } catch {}
        console.warn('[AudioCompress] unexpected error:', err);
        onProgress(100);
        resolve(file);
      }
    };

    reader.onerror = () => {
      onProgress(100);
      resolve(file); // fallback
    };

    reader.readAsArrayBuffer(file);
  });
};

// ═══════════════════════════════════════════════════════════════════════════════
// РАЗДЕЛ 2: Кеш публичных URL аудио
// ═══════════════════════════════════════════════════════════════════════════════

const URL_CACHE_KEY = 'ac_audio_urls_v1';

/** In-memory карта: audioPath → publicUrl */
const _urlMemo = new Map();

/** Загружаем кеш из localStorage при старте */
const _initUrlCache = () => {
  try {
    const raw = localStorage.getItem(URL_CACHE_KEY);
    if (!raw) return;
    JSON.parse(raw).forEach(([k, v]) => _urlMemo.set(k, v));
  } catch {}
};

const _persistUrlCache = () => {
  try {
    // Храним не более 500 записей (защита от разрастания)
    const entries = [..._urlMemo.entries()].slice(-500);
    localStorage.setItem(URL_CACHE_KEY, JSON.stringify(entries));
  } catch {}
};

_initUrlCache();

/**
 * Получить публичный URL аудиофайла из Supabase Storage с кешированием.
 *
 * `getPublicUrl` у Supabase синхронный и не делает сетевых запросов —
 * он просто конструирует URL. Кеш нужен чтобы:
 *   1. Не вызывать метод несколько раз для одного файла за сессию.
 *   2. Мгновенно отдавать URL при повторном открытии приложения.
 *
 * @param {object} supabase - Supabase клиент
 * @param {string} audioPath - путь вида "userId/timestamp.ext"
 * @returns {string|null} publicUrl
 */
export const getAudioPublicUrl = (supabase, audioPath) => {
  if (!audioPath) return null;

  if (_urlMemo.has(audioPath)) {
    return _urlMemo.get(audioPath);
  }

  try {
    const { data: { publicUrl } } = supabase.storage
      .from('audio-files')
      .getPublicUrl(audioPath);

    _urlMemo.set(audioPath, publicUrl);
    _persistUrlCache();
    return publicUrl;
  } catch (e) {
    console.error('[AudioURL] getPublicUrl error:', e);
    return null;
  }
};

/**
 * Предзагрузить URL для массива проектов (вызывается после загрузки списка).
 * Ускоряет первое открытие каждого проекта.
 */
export const prefetchAudioUrls = (supabase, projects) => {
  if (!Array.isArray(projects)) return;
  for (const p of projects) {
    if (p.audio_path && !_urlMemo.has(p.audio_path)) {
      getAudioPublicUrl(supabase, p.audio_path);
    }
  }
};

/**
 * Сброс кеша (используется при logout или удалении проекта).
 * @param {string} [audioPath] - если указан, удаляем только этот путь
 */
export const clearAudioUrlCache = (audioPath) => {
  if (audioPath) {
    _urlMemo.delete(audioPath);
  } else {
    _urlMemo.clear();
    try { localStorage.removeItem(URL_CACHE_KEY); } catch {}
  }
  _persistUrlCache();
};
